import { AudioLines, Coins, Film, ImageIcon, Mic2, Music2, Sparkles, Users, Volume2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const CAPS = [1, 1.3, 1.6, 2, 3];
const usd = (v) => `$${Number(v || 0).toFixed(v < 0.1 && v > 0 ? 3 : 2)}`;

function Row({ icon: Icon, label, value, free, testId }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-900 px-3 py-2 text-xs" data-testid={testId}>
      <span className="flex items-center gap-2 text-neutral-400"><Icon size={12} className="text-neutral-500" /> {label}</span>
      <span className={cn('font-mono', free ? 'text-emerald-300' : 'text-neutral-200')}>{free ? 'free' : usd(value)}</span>
    </div>
  );
}

/** Budget-engine estimate: hard per-episode USD cap, smart split between real motion and free Living Stills. */
export function BudgetEstimate({ est, budget, onBudget, mtv = false, perEpisode = false }) {
  const b = est?.breakdown_usd || {};
  const motionLabel = [est?.motion_480p ? `${est.motion_480p} × 480p` : '', est?.motion_720p ? `${est.motion_720p} × 720p` : ''].filter(Boolean).join(' + ')
    || (est?.living_stills ? `${est.living_stills} shot${est.living_stills > 1 ? 's' : ''} don't fit the budget` : 'none needed');
  return (
    <div className="space-y-3" data-testid="budget-estimate">
      <div className="space-y-1.5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500" data-testid="budget-cap-label">{mtv ? 'Budget for this music video (hard cap)' : perEpisode ? 'Budget per episode (hard cap)' : 'Budget for this run (hard cap)'}</div>
        <div className="flex flex-wrap gap-1.5">
          {(mtv ? CAPS.filter((c) => c <= 1.6) : CAPS).map((c) => (
            <button key={c} type="button" onClick={() => onBudget(c)} data-testid={`budget-cap-${c}`}
              className={cn('rounded-full border px-3 py-1 font-mono text-xs transition-colors duration-200',
                budget === c ? 'border-amber-400 bg-amber-500 text-neutral-900' : 'border-neutral-800 bg-neutral-900 text-neutral-300 hover:border-neutral-600')}>
              ${c.toFixed(2)}
            </button>
          ))}
        </div>
      </div>
      {est && (
        <div className="rounded-lg border border-neutral-800">
          <Row icon={Users} label={`Cast lock portraits × ${est.kit_references}`} value={b.cast_lock} testId="budget-row-cast" />
          <Row icon={ImageIcon} label={`Keyframes · FLUX Klein × ${est.keyframes}`} value={b.keyframes} testId="budget-row-keyframes" />
          <Row icon={AudioLines} label={`Lip-sync · ${est.lipsync_model || 'Kling AI Avatar'} ${est.lipsync_resolution || '720p'} × ${est.lipsync_shots || 0} (${est.lipsync_seconds || 0}s)`} value={b.lipsync} free={!b.lipsync} testId="budget-row-lipsync" />
          <Row icon={Film} label={`Motion · ${est.motion_model || 'Seedance'} ${motionLabel}${mtv ? ' · slow motion' : ''}`} value={b.motion} testId="budget-row-motion" />
          {est.free_shots > 0 && <Row icon={Sparkles} label={`AI-Directed shots × ${est.free_shots}`} free testId="budget-row-free-first" />}
          {est.living_stills > 0 && <Row icon={Sparkles} label={`Living Stills × ${est.living_stills} (cap reached)`} free testId="budget-row-living" />}
          <Row icon={Mic2} label={`Actor voices · ${est.voice_engine || 'ElevenLabs'} · ${est.voice_lines || 0} lines${est.voice_calls ? ` in ${est.voice_calls} calls` : ''}`} value={b.voice} free={!b.voice} testId="budget-row-voice" />
          {!mtv && <Row icon={Volume2} label={`Ambience · ${est.ambience_engine || 'ElevenLabs SFX'} × ${est.ambience_locations || 0} locations`} value={b.ambience} free={!b.ambience} testId="budget-row-ambience" />}
          <Row icon={Music2} label="Music bed, upscale & Final Cut" free testId="budget-row-assembly" />
          <div className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold">
            <span className="flex items-center gap-2"><Coins size={14} className="text-amber-300" /> <span data-testid="budget-estimate-total">≈ {usd(est.usd)} of {usd(est.cap_usd)}</span>
              {est.spent_usd > 0 && <span className="text-[11px] font-normal text-neutral-500" data-testid="budget-spent-before">· {usd(est.spent_usd)} already spent</span>}</span>
            <span className="font-mono text-xs text-neutral-400" data-testid="budget-platform-credits">{est.total_credits} cr</span>
          </div>
        </div>
      )}
      {est && ((est.voice_calls === 0 && est.voice_lines > 0) || (!mtv && est.ambience_locations > 0 && est.ambience_engine?.startsWith('free'))) && (
        <p className="rounded-md border border-amber-400/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200/90" data-testid="budget-model-fallback">
          The premium voice service is not answering right now, so {mtv ? 'voices use' : 'voices use each actor\'s own free backup voice and ambience is made locally'}{mtv ? ' free backup voices' : ''} — nothing stops.
          It is retried automatically every 2 hours; backup lines can be upgraded later.
        </p>
      )}
      <p className="text-[11px] leading-relaxed text-neutral-500">
        Every shot gets real footage: visible dialogue is lip-synced to the actor's own voice, everything else is animated.
        Premium spends what is left on 720p motion. Living Stills appear only if the cap can't cover a shot. Production stops the moment the cap is reached.
      </p>
    </div>
  );
}

/** Live spend vs cap while a budget production runs — and the estimated-vs-actual result afterwards. */
export function CostMeter({ production }) {
  const cap = production?.cap_usd;
  if (production?.engine !== 'budget' || cap == null) return null;
  const spent = production.cost?.usd_actual ?? production.spent_usd ?? 0;
  const pct = Math.min(100, (spent / Math.max(0.01, cap)) * 100);
  const over = production.cost?.overages?.length || 0;
  return (
    <div className="mt-3 space-y-1" data-testid="cost-meter">
      <div className="flex justify-between text-[11px]">
        <span className="text-neutral-400">Spend <span className="font-mono text-neutral-100" data-testid="cost-meter-spent">{usd(spent)}</span> of <span className="font-mono">{usd(cap)}</span>
          {production.cost?.usd_est != null && <span className="text-neutral-500"> · estimated {usd(production.cost.usd_est)}</span>}</span>
        {over > 0 && <span className="text-amber-300" data-testid="cost-meter-overages">{over} pricier fallback{over > 1 ? 's' : ''} logged</span>}
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-neutral-800">
        <div className={cn('h-full transition-[width] duration-700', pct > 95 ? 'bg-red-400' : 'bg-emerald-400')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const PREFS = [['auto', 'Auto'], ['motion', 'Always animate'], ['still', 'Living Still']];

/** Per-shot override for the Budget Director: let it decide, force real motion, or keep it a free Living Still. */
export function MotionPref({ value, onChange, testId }) {
  return (
    <div className="inline-flex rounded-full border border-neutral-800 bg-neutral-900 p-0.5" data-testid={testId}>
      {PREFS.map(([id, label]) => (
        <button key={id} type="button" onClick={() => onChange(id)} data-testid={`${testId}-${id}`}
          className={cn('rounded-full px-2.5 py-0.5 text-[10px] font-medium transition-colors duration-200',
            (value || 'auto') === id ? 'bg-neutral-100 text-neutral-900' : 'text-neutral-400 hover:text-neutral-200')}>
          {label}
        </button>
      ))}
    </div>
  );
}
